/**
 * Deterministic SEO / AEO / Brand Scorer Engine.
 *
 * Supersedes the generic blend in `lib/crm/contentScore.ts` for LONG-FORM
 * web content: instead of one opaque number, every score point here is
 * traceable to a named rule inside one of EIGHT separable weighted
 * dimensions (title, meta, headings, keywords, aeo, readability,
 * internalLinks, brandFidelity). That separability is what powers the
 * Surfer/Clearscope-style per-dimension breakdown UI and the prioritized
 * fix list — and brandFidelity makes "sounds like us" a first-class scored
 * dimension, fed by the same voice/banned-term facts the Brand Brain
 * injects into prompts (see brandBrain/inject.ts + brandBrain/fidelity.ts).
 *
 * PURE by contract: no store, no firebase, no network, no clock, no DOM,
 * no randomness. Same input → same output, always. Pixel widths come from
 * an approximate per-character width table (Arial-like metrics), NOT
 * canvas measurement, so the SERP-preview lint runs headlessly and
 * deterministically. Weights are loadable via `input.weights` so the
 * calibration layer (content/scoreCalibration.ts spirit) can tune them
 * from observed outcomes without touching this engine.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A keyword the content is expected to cover, with a target use range. */
export interface KeywordTarget {
  term: string;
  kind: 'primary' | 'secondary' | 'nlp';
  minUses: number;
  maxUses: number;
}

/** Coverage state for one target term. red=missing/under, green=in range,
 *  yellow=OVERUSE (stuffing) — deliberately not red so the UI reads it as
 *  "dial back", not "add more". */
export interface TermState {
  term: string;
  kind: KeywordTarget['kind'];
  count: number;
  min: number;
  max: number;
  state: 'red' | 'yellow' | 'green';
}

/** One concrete actionable fix, sortable by how many points it recovers. */
export interface FixSuggestion {
  id: string;
  label: string;
  kind:
    | 'add-term'
    | 'shorten-title'
    | 'add-answer'
    | 'split-paragraph'
    | 'add-heading'
    | 'add-faq-schema'
    | 'add-internal-link';
  /** Dimension points (0..100 within its dimension) this fix recovers. */
  expectedGain: number;
  payload: Record<string, unknown>;
}

/** One rule outcome — every point gained or lost has a Finding receipt. */
export interface Finding {
  id: string;
  severity: 'error' | 'warn' | 'ok';
  message: string;
  fix?: FixSuggestion;
}

/** One weighted dimension of the total score. `score` is 0..`max` (always
 *  100 here) BEFORE weighting; `weight` is its share of the blended total. */
export interface SubScore {
  dimension:
    | 'title'
    | 'meta'
    | 'headings'
    | 'keywords'
    | 'aeo'
    | 'readability'
    | 'internalLinks'
    | 'brandFidelity';
  score: number;
  max: number;
  weight: number;
  findings: Finding[];
}

export interface SeoScoreResult {
  total: number;
  grade: 'A++' | 'A+' | 'A' | 'B' | 'C' | 'D' | 'F';
  subScores: SubScore[];
  terms: TermState[];
  prioritizedFixes: FixSuggestion[];
  competitiveDelta?: { typical: number; you: number };
}

export interface ScoreInput {
  title: string;
  meta: string;
  markdown: string;
  targets: KeywordTarget[];
  internalLinkCorpus?: { slug: string; title: string; terms: string[] }[];
  brandFacts?: { voiceTerms: string[]; bannedTerms: string[]; proofPoints: string[] };
  /** Typical competitor score for this SERP — powers competitiveDelta. */
  typicalScore?: number;
  /** Calibration-ready weight overrides; unspecified dims keep defaults. */
  weights?: Partial<Record<SubScore['dimension'], number>>;
}

// ---------------------------------------------------------------------------
// Weights + limits — the transparent knobs. Tune here, never mid-heuristic.
// ---------------------------------------------------------------------------

/** Deterministic default dimension weights (sum 100). Overridable per-call. */
export const DEFAULT_WEIGHTS: Readonly<Record<SubScore['dimension'], number>> = {
  title: 10,
  meta: 8,
  headings: 12,
  keywords: 25,
  aeo: 15,
  readability: 12,
  internalLinks: 8,
  brandFidelity: 10,
};

/** SERP truncation bounds (approximate desktop Google, Arial-like). */
export const TITLE_PIXEL_LIMIT = 580;
export const META_PIXEL_LIMIT = 990;
export const TITLE_CHAR_MAX = 60;
export const TITLE_CHAR_MIN = 30;
export const META_CHAR_MIN = 120;
export const META_CHAR_MAX = 160;

/** An answer under a question H2 must land inside this many words. */
export const AEO_ANSWER_WORD_LIMIT = 60;

/** A heading-less block past this word count is a wall of text. */
export const WALL_OF_TEXT_WORDS = 200;

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const round1 = (n: number) => Math.round(n * 10) / 10;

// ---------------------------------------------------------------------------
// Text utilities (internal, deterministic)
// ---------------------------------------------------------------------------

/** Markdown → plain prose (headings/lists/links/tables/code stripped). */
function stripMarkdown(md: string): string {
  return (md ?? '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\|/g, ' ')
    .replace(/[*_>]/g, '');
}

function words(text: string): string[] {
  return text.split(/\s+/).filter((w) => w.length > 0);
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Case-insensitive whole-phrase occurrence count. */
function countOccurrences(haystack: string, term: string): number {
  const t = term.trim();
  if (t.length === 0) return 0;
  const re = new RegExp(`\\b${escapeRegExp(t)}\\b`, 'gi');
  return (haystack.match(re) ?? []).length;
}

interface HeadingLine {
  level: number;
  text: string;
  line: number;
}

function parseHeadings(markdown: string): HeadingLine[] {
  const out: HeadingLine[] = [];
  const lines = (markdown ?? '').split('\n');
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^(#{1,6})\s+(.*\S)\s*$/);
    if (m) out.push({ level: m[1].length, text: m[2].trim(), line: i });
  }
  return out;
}

// ---------------------------------------------------------------------------
// pixelWidth — approximate per-char width table, no DOM
// ---------------------------------------------------------------------------

/**
 * Approximate rendered width of SERP text in pixels. Title metrics model a
 * ~20px Arial-like face; meta scales the same table to its smaller face.
 * Deliberately a lookup table (no canvas/DOM) so it is pure + testable.
 */
export function pixelWidth(text: string, kind: 'title' | 'meta'): number {
  let w = 0;
  for (const ch of text ?? '') {
    if (ch === ' ') w += 6;
    else if (/[ilI.,:;!|'’‘`]/.test(ch)) w += 5;
    else if (/[jtfr()[\]{}"-]/.test(ch)) w += 7;
    else if (/[mwMW@]/.test(ch)) w += 17;
    else if (/[A-HJ-LN-VX-Z]/.test(ch)) w += 13;
    else if (/[0-9]/.test(ch)) w += 11;
    else w += 10;
  }
  return Math.round(kind === 'meta' ? w * 0.65 : w);
}

// ---------------------------------------------------------------------------
// Keyword coverage engine
// ---------------------------------------------------------------------------

/**
 * Count each target term in the prose (headings included — they count as
 * usage) and classify: below min → red, within [min,max] → green, above
 * max → yellow (overuse/stuffing). Order mirrors the targets array.
 */
export function computeKeywordCoverage(
  markdown: string,
  targets: KeywordTarget[],
): TermState[] {
  const plain = stripMarkdown(markdown);
  return targets.map((t) => {
    const count = countOccurrences(plain, t.term);
    const state: TermState['state'] =
      count < t.minUses ? 'red' : count > t.maxUses ? 'yellow' : 'green';
    return { term: t.term, kind: t.kind, count, min: t.minUses, max: t.maxUses, state };
  });
}

// ---------------------------------------------------------------------------
// Readability
// ---------------------------------------------------------------------------

function countSyllables(word: string): number {
  const w = word.toLowerCase().replace(/[^a-z]/g, '');
  if (w.length === 0) return 0;
  const groups = w.match(/[aeiouy]+/g) ?? [];
  let n = groups.length;
  if (n > 1 && w.endsWith('e') && !w.endsWith('le')) n -= 1;
  return Math.max(1, n);
}

/**
 * Flesch–Kincaid grade + the concrete drag factors (long sentences,
 * passive voice, paragraph bloat) and a 0..100 readability score.
 * Empty prose scores 0 honestly — never a fabricated grade.
 */
export function computeReadability(markdown: string): {
  fleschKincaidGrade: number;
  longSentences: number;
  passiveHits: number;
  avgParaWords: number;
  score: number;
} {
  const plain = stripMarkdown(markdown);
  const sentences = plain
    .split(/[.!?]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const allWords = words(plain);

  if (allWords.length === 0 || sentences.length === 0) {
    return { fleschKincaidGrade: 0, longSentences: 0, passiveHits: 0, avgParaWords: 0, score: 0 };
  }

  const syllables = allWords.reduce((sum, w) => sum + countSyllables(w), 0);
  const grade = round1(
    Math.max(
      0,
      0.39 * (allWords.length / sentences.length) +
        11.8 * (syllables / allWords.length) -
        15.59,
    ),
  );

  const longSentences = sentences.filter((s) => words(s).length > 25).length;
  const passiveHits = (plain.match(/\b(?:is|are|was|were|be|been|being)\s+\w+(?:ed|en)\b/gi) ?? [])
    .length;

  const paragraphs = (markdown ?? '')
    .split(/\n\s*\n/)
    .map((p) => stripMarkdown(p).trim())
    .filter((p) => p.length > 0);
  const avgParaWords =
    paragraphs.length === 0
      ? 0
      : Math.round(paragraphs.reduce((s, p) => s + words(p).length, 0) / paragraphs.length);

  let score = 100;
  score -= Math.min(30, Math.max(0, grade - 9) * 6);
  score -= Math.min(25, longSentences * 6);
  score -= Math.min(15, passiveHits * 4);
  if (avgParaWords > 90) score -= 15;

  return {
    fleschKincaidGrade: grade,
    longSentences,
    passiveHits,
    avgParaWords,
    score: Math.round(clamp(score, 0, 100)),
  };
}

// ---------------------------------------------------------------------------
// SERP-preview lint: title + meta
// ---------------------------------------------------------------------------

function makeSubScore(
  dimension: SubScore['dimension'],
  score: number,
  findings: Finding[],
): SubScore {
  return {
    dimension,
    score: Math.round(clamp(score, 0, 100)),
    max: 100,
    weight: DEFAULT_WEIGHTS[dimension],
    findings,
  };
}

/** SERP title lint: truncation (chars + pixels), length floor, primary-keyword presence. */
export function lintTitle(title: string, primary?: string): SubScore {
  const findings: Finding[] = [];
  let score = 100;
  const t = (title ?? '').trim();

  if (t.length === 0) {
    findings.push({ id: 'title-empty', severity: 'error', message: 'Title is empty.' });
    return makeSubScore('title', 0, findings);
  }

  const px = pixelWidth(t, 'title');
  if (t.length > TITLE_CHAR_MAX || px > TITLE_PIXEL_LIMIT) {
    score -= 35;
    findings.push({
      id: 'title-truncated',
      severity: 'error',
      message: `Title will truncate in the SERP: ${t.length} chars / ~${px}px (limits ${TITLE_CHAR_MAX} chars, ${TITLE_PIXEL_LIMIT}px).`,
      fix: {
        id: 'fix-shorten-title',
        label: `Shorten title to ≤${TITLE_CHAR_MAX} characters`,
        kind: 'shorten-title',
        expectedGain: 35,
        payload: { chars: t.length, pixels: px, charLimit: TITLE_CHAR_MAX, pixelLimit: TITLE_PIXEL_LIMIT },
      },
    });
  } else if (t.length < TITLE_CHAR_MIN) {
    score -= 15;
    findings.push({
      id: 'title-too-short',
      severity: 'warn',
      message: `Title is only ${t.length} chars — under ${TITLE_CHAR_MIN} wastes SERP real estate.`,
    });
  } else {
    findings.push({
      id: 'title-length-ok',
      severity: 'ok',
      message: `Title fits the SERP (${t.length} chars, ~${px}px).`,
    });
  }

  if (primary && primary.trim().length > 0) {
    if (countOccurrences(t, primary) === 0) {
      score -= 35;
      findings.push({
        id: 'title-missing-primary',
        severity: 'error',
        message: `Primary keyword “${primary}” is missing from the title.`,
        fix: {
          id: 'fix-title-add-primary',
          label: `Add “${primary}” to the title`,
          kind: 'add-term',
          expectedGain: 35,
          payload: { term: primary, where: 'title' },
        },
      });
    } else {
      findings.push({
        id: 'title-has-primary',
        severity: 'ok',
        message: `Primary keyword “${primary}” appears in the title.`,
      });
    }
  }

  return makeSubScore('title', score, findings);
}

/** Meta-description lint: length window + pixel truncation. */
export function lintMeta(meta: string): SubScore {
  const findings: Finding[] = [];
  let score = 100;
  const m = (meta ?? '').trim();

  if (m.length === 0) {
    findings.push({ id: 'meta-empty', severity: 'error', message: 'Meta description is empty.' });
    return makeSubScore('meta', 0, findings);
  }

  const px = pixelWidth(m, 'meta');
  if (m.length < META_CHAR_MIN) {
    score -= 30;
    findings.push({
      id: 'meta-too-short',
      severity: 'warn',
      message: `Meta is ${m.length} chars — under ${META_CHAR_MIN} looks thin and gets rewritten by Google.`,
    });
  } else if (m.length > META_CHAR_MAX || px > META_PIXEL_LIMIT) {
    score -= 30;
    findings.push({
      id: 'meta-truncated',
      severity: 'warn',
      message: `Meta will truncate: ${m.length} chars / ~${px}px (limits ${META_CHAR_MAX} chars, ${META_PIXEL_LIMIT}px).`,
      fix: {
        id: 'fix-shorten-meta',
        label: `Trim meta to ≤${META_CHAR_MAX} characters`,
        kind: 'shorten-title',
        expectedGain: 30,
        payload: { field: 'meta', chars: m.length, pixels: px, charLimit: META_CHAR_MAX },
      },
    });
  } else {
    findings.push({
      id: 'meta-length-ok',
      severity: 'ok',
      message: `Meta fits the SERP (${m.length} chars, ~${px}px).`,
    });
  }

  return makeSubScore('meta', score, findings);
}

// ---------------------------------------------------------------------------
// Heading hierarchy
// ---------------------------------------------------------------------------

/**
 * Structural lint: exactly one H1, no skipped levels (H2→H4), primary
 * keyword in the H1 (when supplied), and wall-of-text detection — any
 * heading-less block past WALL_OF_TEXT_WORDS words needs a subhead.
 */
export function validateHeadingHierarchy(markdown: string, primary?: string): SubScore {
  const findings: Finding[] = [];
  let score = 100;
  const headings = parseHeadings(markdown ?? '');
  const h1s = headings.filter((h) => h.level === 1);

  if (h1s.length === 0) {
    score -= 25;
    findings.push({
      id: 'headings-no-h1',
      severity: 'error',
      message: 'No H1 found — the page needs exactly one.',
      fix: {
        id: 'fix-add-h1',
        label: 'Add a single H1',
        kind: 'add-heading',
        expectedGain: 25,
        payload: { level: 1 },
      },
    });
  } else if (h1s.length > 1) {
    score -= 25;
    findings.push({
      id: 'headings-multiple-h1',
      severity: 'error',
      message: `${h1s.length} H1s found — a page must have exactly one.`,
    });
  } else {
    findings.push({ id: 'headings-one-h1', severity: 'ok', message: 'Exactly one H1.' });
  }

  for (let i = 1; i < headings.length; i++) {
    if (headings[i].level > headings[i - 1].level + 1) {
      score -= 20;
      findings.push({
        id: `headings-skip-level-${headings[i].line}`,
        severity: 'error',
        message: `Heading level skips from H${headings[i - 1].level} to H${headings[i].level} at “${headings[i].text}”.`,
        fix: {
          id: `fix-heading-skip-${headings[i].line}`,
          label: `Insert an H${headings[i - 1].level + 1} before “${headings[i].text}”`,
          kind: 'add-heading',
          expectedGain: 20,
          payload: { after: headings[i - 1].text, level: headings[i - 1].level + 1 },
        },
      });
    }
  }

  if (primary && primary.trim().length > 0 && h1s.length === 1) {
    if (countOccurrences(h1s[0].text, primary) === 0) {
      score -= 15;
      findings.push({
        id: 'headings-h1-missing-primary',
        severity: 'warn',
        message: `H1 “${h1s[0].text}” does not contain the primary keyword “${primary}”.`,
        fix: {
          id: 'fix-h1-add-primary',
          label: `Work “${primary}” into the H1`,
          kind: 'add-term',
          expectedGain: 15,
          payload: { term: primary, where: 'h1' },
        },
      });
    } else {
      findings.push({
        id: 'headings-h1-has-primary',
        severity: 'ok',
        message: 'Primary keyword appears in the H1.',
      });
    }
  }

  // Wall-of-text: word count of each block between consecutive headings.
  const lines = (markdown ?? '').split('\n');
  const boundaries = [...headings.map((h) => h.line), lines.length];
  const starts = headings.length > 0 ? headings.map((h) => h.line + 1) : [0];
  const blockEnds = headings.length > 0 ? boundaries.slice(1) : [lines.length];
  for (let i = 0; i < starts.length; i++) {
    const block = lines.slice(starts[i], blockEnds[i]).join('\n');
    const n = words(stripMarkdown(block)).length;
    if (n > WALL_OF_TEXT_WORDS) {
      score -= 15;
      const under = headings[i] ? `under “${headings[i].text}”` : 'before the first heading';
      findings.push({
        id: `headings-wall-of-text-${i}`,
        severity: 'warn',
        message: `${n}-word block ${under} has no subheading — break it up.`,
        fix: {
          id: `fix-wall-of-text-${i}`,
          label: `Add a subheading inside the ${n}-word block`,
          kind: 'add-heading',
          expectedGain: 15,
          payload: { blockWords: n, under: headings[i]?.text ?? null },
        },
      });
    }
  }

  return makeSubScore('headings', score, findings);
}

// ---------------------------------------------------------------------------
// AEO detectors — answer-engine optimization
// ---------------------------------------------------------------------------

const QUESTION_START =
  /^(how|what|why|when|where|who|which|can|does|do|is|are|should|will)\b/i;

function isQuestionHeading(text: string): boolean {
  return /\?\s*$/.test(text) || QUESTION_START.test(text.trim());
}

/** First prose paragraph after a heading line (stops at the next heading). */
function firstParagraphAfter(lines: string[], headingLine: number): string {
  const para: string[] = [];
  for (let i = headingLine + 1; i < lines.length; i++) {
    const line = lines[i];
    if (/^#{1,6}\s/.test(line)) break;
    if (line.trim().length === 0) {
      if (para.length > 0) break;
      continue;
    }
    para.push(line.trim());
  }
  return para.join(' ');
}

/**
 * Answer-engine readiness: question-shaped H2s, an answer-first paragraph
 * inside AEO_ANSWER_WORD_LIMIT words under each one, and extractable
 * structure (lists/tables). Suggests FAQ JSON-LD when Q&A pairs exist.
 */
export function detectAEO(markdown: string): SubScore {
  const findings: Finding[] = [];
  let score = 100;
  const md = markdown ?? '';
  const lines = md.split('\n');
  const questionH2s = parseHeadings(md).filter(
    (h) => h.level === 2 && isQuestionHeading(h.text),
  );

  if (questionH2s.length === 0) {
    score -= 35;
    findings.push({
      id: 'aeo-no-question-h2',
      severity: 'warn',
      message: 'No question-shaped H2s — answer engines have nothing to latch onto.',
      fix: {
        id: 'fix-add-question-h2',
        label: 'Add a question-shaped H2 (e.g. “How does … work?”)',
        kind: 'add-heading',
        expectedGain: 35,
        payload: { level: 2, shape: 'question' },
      },
    });
  }

  let answeredPairs = 0;
  for (const h of questionH2s) {
    const para = stripMarkdown(firstParagraphAfter(lines, h.line));
    const n = words(para).length;
    if (n === 0) {
      score -= 30;
      findings.push({
        id: `aeo-missing-answer-${h.line}`,
        severity: 'error',
        message: `No answer paragraph under “${h.text}”.`,
        fix: {
          id: `fix-add-answer-${h.line}`,
          label: `Write a ≤${AEO_ANSWER_WORD_LIMIT}-word direct answer under “${h.text}”`,
          kind: 'add-answer',
          expectedGain: 30,
          payload: { heading: h.text, wordLimit: AEO_ANSWER_WORD_LIMIT },
        },
      });
    } else if (n > AEO_ANSWER_WORD_LIMIT) {
      score -= 25;
      findings.push({
        id: `aeo-buried-answer-${h.line}`,
        severity: 'error',
        message: `Answer under “${h.text}” is buried — first paragraph runs ${n} words (limit ${AEO_ANSWER_WORD_LIMIT}).`,
        fix: {
          id: `fix-answer-first-${h.line}`,
          label: `Lead “${h.text}” with a ≤${AEO_ANSWER_WORD_LIMIT}-word direct answer`,
          kind: 'add-answer',
          expectedGain: 25,
          payload: { heading: h.text, currentWords: n, wordLimit: AEO_ANSWER_WORD_LIMIT },
        },
      });
    } else {
      answeredPairs += 1;
      findings.push({
        id: `aeo-answer-first-${h.line}`,
        severity: 'ok',
        message: `“${h.text}” is answered in ${n} words — extractable.`,
      });
    }
  }

  const hasList = /^\s*(?:[-*+]|\d+\.)\s+/m.test(md);
  if (!hasList) {
    score -= 10;
    findings.push({
      id: 'aeo-no-list',
      severity: 'warn',
      message: 'No list structure — lists are the easiest snippet format to win.',
    });
  }
  const hasTable = /^\|.+\|\s*$/m.test(md);
  if (!hasTable) {
    score -= 5;
    findings.push({
      id: 'aeo-no-table',
      severity: 'warn',
      message: 'No table — comparison tables earn rich extracts.',
    });
  }

  if (answeredPairs > 0) {
    findings.push({
      id: 'aeo-faq-schema',
      severity: 'ok',
      message: `${answeredPairs} extractable Q&A pair(s) — ship FAQPage JSON-LD to claim them.`,
      fix: {
        id: 'fix-add-faq-schema',
        label: 'Attach FAQPage JSON-LD built from the Q&A pairs',
        kind: 'add-faq-schema',
        expectedGain: 5,
        payload: { pairs: answeredPairs },
      },
    });
  }

  return makeSubScore('aeo', score, findings);
}

// ---------------------------------------------------------------------------
// JSON-LD builders
// ---------------------------------------------------------------------------

/** FAQPage JSON-LD from Q&A pairs. Deterministic, schema.org-shaped. */
export function buildFaqJsonLd(pairs: { q: string; a: string }[]): string {
  return JSON.stringify(
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: pairs.map((p) => ({
        '@type': 'Question',
        name: p.q,
        acceptedAnswer: { '@type': 'Answer', text: p.a },
      })),
    },
    null,
    2,
  );
}

/** Article JSON-LD from the SERP fields. */
export function buildArticleJsonLd(input: { title: string; meta: string }): string {
  return JSON.stringify(
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: input.title,
      description: input.meta,
    },
    null,
    2,
  );
}

// ---------------------------------------------------------------------------
// Grade + fix prioritization
// ---------------------------------------------------------------------------

/** Letter grade from a 0..100 total. Boundaries are pinned by tests. */
export function gradeFromTotal(total: number): SeoScoreResult['grade'] {
  if (total >= 97) return 'A++';
  if (total >= 92) return 'A+';
  if (total >= 85) return 'A';
  if (total >= 70) return 'B';
  if (total >= 55) return 'C';
  if (total >= 40) return 'D';
  return 'F';
}

/**
 * Flatten every finding's fix across all dimensions and sort by
 * expectedGain DESC (tie → id ASC, so the order never flickers).
 */
export function prioritizeFixes(subScores: SubScore[]): FixSuggestion[] {
  const fixes: FixSuggestion[] = [];
  for (const sub of subScores) {
    for (const f of sub.findings) {
      if (f.fix) fixes.push(f.fix);
    }
  }
  return fixes.sort(
    (a, b) => b.expectedGain - a.expectedGain || a.id.localeCompare(b.id),
  );
}

// ---------------------------------------------------------------------------
// Dimension builders that need full ScoreInput context
// ---------------------------------------------------------------------------

function scoreKeywords(terms: TermState[]): SubScore {
  const findings: Finding[] = [];
  if (terms.length === 0) {
    findings.push({
      id: 'keywords-no-targets',
      severity: 'ok',
      message: 'No keyword targets supplied — coverage not graded.',
    });
    return makeSubScore('keywords', 100, findings);
  }

  const kindWeight = { primary: 3, secondary: 2, nlp: 1 } as const;
  let earned = 0;
  let possible = 0;
  for (const t of terms) {
    const w = kindWeight[t.kind];
    possible += w;
    if (t.state === 'green') {
      earned += w;
      findings.push({
        id: `kw-green-${t.term}`,
        severity: 'ok',
        message: `“${t.term}” used ${t.count}× — inside its ${t.min}–${t.max} target.`,
      });
    } else if (t.state === 'yellow') {
      earned += w * 0.5;
      findings.push({
        id: `kw-overuse-${t.term}`,
        severity: 'warn',
        message: `“${t.term}” used ${t.count}× — over its ${t.max} cap (reads as stuffing).`,
      });
    } else {
      const gainPct = Math.round((w / Math.max(1, possible)) * 100);
      findings.push({
        id: `kw-missing-${t.term}`,
        severity: 'error',
        message: `“${t.term}” used ${t.count}× — needs at least ${t.min}.`,
        fix: {
          id: `fix-add-term-${t.term}`,
          label: `Use “${t.term}” at least ${t.min - t.count} more time(s)`,
          kind: 'add-term',
          expectedGain: Math.max(5, gainPct),
          payload: { term: t.term, kind: t.kind, add: t.min - t.count },
        },
      });
    }
  }
  return makeSubScore('keywords', (earned / possible) * 100, findings);
}

function scoreReadabilityDim(markdown: string): SubScore {
  const r = computeReadability(markdown);
  const findings: Finding[] = [];
  if (words(stripMarkdown(markdown)).length === 0) {
    findings.push({ id: 'read-empty', severity: 'error', message: 'No prose to grade.' });
    return makeSubScore('readability', 0, findings);
  }
  findings.push({
    id: 'read-grade',
    severity: r.fleschKincaidGrade > 12 ? 'warn' : 'ok',
    message: `Flesch–Kincaid grade ${r.fleschKincaidGrade} (target ≤9 for web prose).`,
  });
  if (r.longSentences > 0) {
    findings.push({
      id: 'read-long-sentences',
      severity: 'warn',
      message: `${r.longSentences} sentence(s) over 25 words.`,
    });
  }
  if (r.passiveHits > 0) {
    findings.push({
      id: 'read-passive',
      severity: 'warn',
      message: `${r.passiveHits} passive-voice construction(s).`,
    });
  }
  if (r.avgParaWords > 90) {
    findings.push({
      id: 'read-bloated-paragraphs',
      severity: 'warn',
      message: `Average paragraph runs ${r.avgParaWords} words — split for scanability.`,
      fix: {
        id: 'fix-split-paragraphs',
        label: 'Split the longest paragraphs',
        kind: 'split-paragraph',
        expectedGain: 15,
        payload: { avgParaWords: r.avgParaWords },
      },
    });
  }
  return makeSubScore('readability', r.score, findings);
}

function scoreInternalLinks(
  markdown: string,
  corpus?: { slug: string; title: string; terms: string[] }[],
): SubScore {
  const findings: Finding[] = [];
  const md = markdown ?? '';
  const linkTargets = [...md.matchAll(/\[[^\]]*\]\(([^)\s]+)[^)]*\)/g)].map((m) => m[1]);
  const corpusSlugs = new Set((corpus ?? []).map((c) => c.slug));
  const internal = linkTargets.filter(
    (href) =>
      href.startsWith('/') ||
      href.startsWith('#') ||
      corpusSlugs.has(href) ||
      corpusSlugs.has(href.replace(/^\//, '')),
  );

  if (internal.length >= 2) {
    findings.push({
      id: 'links-ok',
      severity: 'ok',
      message: `${internal.length} internal links found.`,
    });
    return makeSubScore('internalLinks', 100, findings);
  }

  // Deterministic best suggestion: corpus item whose terms appear most in
  // the prose; ties break to slug ASC.
  let suggestion: { slug: string; title: string; matched: number } | undefined;
  if (corpus && corpus.length > 0) {
    const plain = stripMarkdown(md);
    const ranked = corpus
      .map((c) => ({
        slug: c.slug,
        title: c.title,
        matched: c.terms.filter((t) => countOccurrences(plain, t) > 0).length,
      }))
      .sort((a, b) => b.matched - a.matched || a.slug.localeCompare(b.slug));
    suggestion = ranked[0];
  }

  if (internal.length === 1) {
    findings.push({
      id: 'links-thin',
      severity: 'warn',
      message: 'Only 1 internal link — aim for 2+.',
      fix: suggestion
        ? {
            id: `fix-add-internal-link-${suggestion.slug}`,
            label: `Link to “${suggestion.title}”`,
            kind: 'add-internal-link',
            expectedGain: 30,
            payload: { slug: suggestion.slug, title: suggestion.title },
          }
        : undefined,
    });
    return makeSubScore('internalLinks', 70, findings);
  }

  findings.push({
    id: 'links-none',
    severity: corpus && corpus.length > 0 ? 'error' : 'warn',
    message: 'No internal links found.',
    fix: suggestion
      ? {
          id: `fix-add-internal-link-${suggestion.slug}`,
          label: `Link to “${suggestion.title}”`,
          kind: 'add-internal-link',
          expectedGain: 60,
          payload: { slug: suggestion.slug, title: suggestion.title },
        }
      : undefined,
  });
  return makeSubScore('internalLinks', corpus && corpus.length > 0 ? 40 : 80, findings);
}

function scoreBrandFidelityDim(
  markdown: string,
  title: string,
  facts?: { voiceTerms: string[]; bannedTerms: string[]; proofPoints: string[] },
): SubScore {
  const findings: Finding[] = [];
  if (!facts) {
    findings.push({
      id: 'brand-no-facts',
      severity: 'ok',
      message: 'No brand facts supplied — fidelity not graded.',
    });
    return makeSubScore('brandFidelity', 100, findings);
  }

  const haystack = `${title}\n${stripMarkdown(markdown)}`;
  let score = 100;

  // Voice-term presence — the positive half of fidelity.
  if (facts.voiceTerms.length > 0) {
    const present = facts.voiceTerms.filter((t) => countOccurrences(haystack, t) > 0);
    const fraction = present.length / facts.voiceTerms.length;
    score -= Math.round((1 - fraction) * 40);
    if (fraction < 1) {
      const missing = facts.voiceTerms.filter((t) => countOccurrences(haystack, t) === 0);
      findings.push({
        id: 'brand-voice-gaps',
        severity: 'warn',
        message: `Missing voice terms: ${missing.join(', ')}.`,
        fix: {
          id: `fix-add-voice-term-${missing[0]}`,
          label: `Work “${missing[0]}” into the copy`,
          kind: 'add-term',
          expectedGain: Math.max(5, Math.round(40 / facts.voiceTerms.length)),
          payload: { term: missing[0], missing },
        },
      });
    } else {
      findings.push({
        id: 'brand-voice-ok',
        severity: 'ok',
        message: 'All voice terms present.',
      });
    }
  }

  // Banned-term penalty — the hard half. Mirrors brandBrain/fidelity.ts.
  for (const banned of facts.bannedTerms) {
    if (countOccurrences(haystack, banned) > 0) {
      score -= 30;
      findings.push({
        id: `brand-banned-${banned}`,
        severity: 'error',
        message: `Uses banned term “${banned}”.`,
      });
    }
  }

  // Proof points: at least one concrete receipt should survive editing.
  if (facts.proofPoints.length > 0) {
    const anyProof = facts.proofPoints.some((p) => countOccurrences(haystack, p) > 0);
    if (!anyProof) {
      score -= 10;
      findings.push({
        id: 'brand-no-proof',
        severity: 'warn',
        message: 'None of the brand proof points appear in the copy.',
      });
    } else {
      findings.push({
        id: 'brand-proof-ok',
        severity: 'ok',
        message: 'At least one brand proof point is present.',
      });
    }
  }

  if (findings.length === 0) {
    findings.push({ id: 'brand-clean', severity: 'ok', message: 'No brand rules violated.' });
  }
  return makeSubScore('brandFidelity', score, findings);
}

// ---------------------------------------------------------------------------
// scoreContent — the one entry the UI calls
// ---------------------------------------------------------------------------

/**
 * Grade the full document across all eight dimensions, blend with the
 * (overridable) weights, and return the traceable result: per-dimension
 * findings, term coverage states, and a prioritized fix list. Pure and
 * deterministic — identical input yields an identical result object.
 */
export function scoreContent(input: ScoreInput): SeoScoreResult {
  const primary = input.targets.find((t) => t.kind === 'primary')?.term;
  const terms = computeKeywordCoverage(input.markdown, input.targets);

  const subScores: SubScore[] = [
    lintTitle(input.title, primary),
    lintMeta(input.meta),
    validateHeadingHierarchy(input.markdown, primary),
    scoreKeywords(terms),
    detectAEO(input.markdown),
    scoreReadabilityDim(input.markdown),
    scoreInternalLinks(input.markdown, input.internalLinkCorpus),
    scoreBrandFidelityDim(input.markdown, input.title, input.brandFacts),
  ];

  // Apply calibration weights (defaults for anything unspecified).
  for (const sub of subScores) {
    const override = input.weights?.[sub.dimension];
    sub.weight = typeof override === 'number' && override >= 0 ? override : DEFAULT_WEIGHTS[sub.dimension];
  }

  const weightSum = subScores.reduce((s, d) => s + d.weight, 0);
  const total =
    weightSum === 0
      ? 0
      : Math.round(
          subScores.reduce((s, d) => s + (d.score / d.max) * d.weight, 0) / weightSum * 100,
        );

  const result: SeoScoreResult = {
    total,
    grade: gradeFromTotal(total),
    subScores,
    terms,
    prioritizedFixes: prioritizeFixes(subScores),
  };
  if (typeof input.typicalScore === 'number') {
    result.competitiveDelta = { typical: input.typicalScore, you: total };
  }
  return result;
}
